import { ChevronRight } from 'lucide-react'
import type { CSSProperties } from 'react'
import { formatDealValue } from '@/utils/dealDisplay'
import { cn } from '@/utils/cn'
import type { JourneyMacroId } from '@/components/Deals/dealsJourney.constants'
import { getJourneyPipelineSummary } from '@/components/Deals/journeyPipelineMetrics'
import type { JourneyPipelineStep } from '@/components/Deals/journeyPipelineMetrics'
import type { JourneyPipelineBarProps } from '@/components/Deals/journeyPipelineBar.types'

function PipelineStepButton({
  step,
  flexWeight,
  onMacroClick,
}: {
  step: JourneyPipelineStep
  flexWeight: number
  onMacroClick?: (macroId: JourneyMacroId) => void
}) {
  const { macro, count, pct, atRisk } = step

  return (
    <button
      type='button'
      className={cn(
        'group/step relative flex min-w-[4.5rem] flex-col rounded-lg border border-[var(--border)] bg-[var(--bg-dark)]/35 px-2.5 py-2 text-left transition-colors',
        'hover:border-[color-mix(in_srgb,var(--step-color)_35%,var(--border))] hover:bg-[color-mix(in_srgb,var(--step-color)_6%,var(--bg-dark))]',
        count === 0 && 'opacity-60',
      )}
      style={
        {
          flex: `${flexWeight} 1 0`,
          '--step-color': macro.color,
        } as CSSProperties
      }
      title={`${macro.label}: ${count} negociações (${pct}%)`}
      aria-label={`${macro.label}: ${count} negociações`}
      onClick={() => onMacroClick?.(macro.id)}
    >
      <span
        className='absolute inset-x-2.5 top-0 h-0.5 rounded-full'
        style={{ backgroundColor: macro.color }}
        aria-hidden
      />
      <span className='mt-1 truncate text-[10px] font-medium text-text-muted'>{macro.label}</span>
      <span className='mt-0.5 flex items-baseline gap-1.5'>
        <span className='text-lg font-bold tabular-nums leading-none text-text-primary'>{count}</span>
        <span className='text-[10px] tabular-nums text-text-muted'>{pct}%</span>
      </span>
      {atRisk > 0 ? (
        <span className='mt-1 text-[10px] font-semibold text-warning'>{atRisk} em risco</span>
      ) : null}
    </button>
  )
}

export function JourneyPipelineBar({
  macros,
  grouped,
  total,
  areValuesVisible = false,
  onMacroClick,
}: JourneyPipelineBarProps) {
  const { totalValue, totalAtRisk, steps } = getJourneyPipelineSummary(macros, grouped)

  return (
    <div
      className='deals-journey-pipeline overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--bg-card)] shadow-sm'
      aria-label='Resumo do pipeline'
    >
      <div className='flex flex-wrap items-end justify-between gap-3 border-b border-[var(--border)] px-3 py-2.5'>
        <div className='min-w-0'>
          <p className='text-[10px] font-medium uppercase tracking-wide text-text-muted'>Pipeline</p>
          <p className='mt-0.5 text-sm font-semibold text-text-primary'>
            {total} negociaç{total === 1 ? 'ão' : 'ões'} ativas
          </p>
        </div>

        <dl className='flex shrink-0 items-end gap-4 text-right'>
          <div>
            <dt className='text-[10px] text-text-muted'>Valor total</dt>
            <dd className='text-sm font-bold tabular-nums text-text-primary'>
              {formatDealValue(totalValue, areValuesVisible, true)}
            </dd>
          </div>
          {totalAtRisk > 0 ? (
            <div>
              <dt className='text-[10px] text-text-muted'>Em risco</dt>
              <dd className='text-sm font-bold tabular-nums text-warning'>{totalAtRisk}</dd>
            </div>
          ) : null}
        </dl>
      </div>

      {total > 0 ? (
        <div className='flex items-stretch gap-1.5 overflow-x-auto px-3 py-3'>
          {steps.map((step, index) => (
            <div key={step.macro.id} className='flex min-w-0 flex-1 items-stretch gap-1.5'>
              {index > 0 ? (
                <ChevronRight
                  size={14}
                  className='hidden shrink-0 self-center text-text-muted/50 sm:block'
                  aria-hidden
                />
              ) : null}
              <PipelineStepButton
                step={step}
                flexWeight={Math.max(step.count, 1)}
                onMacroClick={onMacroClick}
              />
            </div>
          ))}
        </div>
      ) : (
        <p className='px-3 py-4 text-[11px] text-text-muted'>Pipeline vazio nesta visão</p>
      )}
    </div>
  )
}
